import { useEffect, useMemo, useState } from "react";

import { AxiosErrorWithData } from "utils/errorUtil";
import { v4 as uuidv4 } from "uuid";

import { ToastType, showToast } from "components/shared/Toast";

import { PadActions, PadScenarioContextState } from "../contexts/PadScenarioProvider";
import useRunScenario from "./postRunScenario";
import useAddPadLayer from "./useAddPadLayer";
import useExportScenarios from "./useExportScenarios";
import useStickData from "./useStickData";

export function usePadScenarioController(
  state: PadScenarioContextState,
  dispatch: React.Dispatch<PadActions>
) {
  const { mutateAsync: runScenario, isLoading } = useRunScenario();
  const { mutateAsync: addPadLayer } = useAddPadLayer();
  const { exportScenarios } = useExportScenarios();

  const [showNewScenarioModal, setShowNewScenarioModal] = useState(false);
  const [editId, setEditId] = useState<string>();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Prepare request for useStickData
  const stickDataRequest = useMemo(() => {
    const scenarioId = state.scenario?._id?.$oid;
    if (!scenarioId || state.scenarioFields.length === 0) {
      return null;
    }

    const request = {
      scenarioIds: [scenarioId],
      fields: state.scenarioFields.map((field) => ({ fieldName: field.field }))
    };

    return request;
  }, [state.scenario?._id?.$oid, state.scenarioFields]);

  const { sticks } = state;
  // Fetch stick data from data-svc-rs
  const {
    data: stickData,
    isLoading: isLoadingStickData,
    error: stickDataError
  } = useStickData(
    stickDataRequest || { scenarioIds: [], fields: [] },
    !!stickDataRequest,
    sticks.map((stick) => stick.id)
  );

  // Update context with stick data whenever it changes
  useEffect(() => {
    if (stickData) {
      dispatch({ type: "SET_STICK_DATA", payload: stickData });
    }
  }, [stickData, dispatch]);

  // Update context with loading state
  useEffect(() => {
    dispatch({ type: "SET_STICK_DATA_LOADING", payload: isLoadingStickData });
  }, [isLoadingStickData, dispatch]);

  // Update context with error state
  useEffect(() => {
    dispatch({ type: "SET_STICK_DATA_ERROR", payload: stickDataError });
  }, [stickDataError, dispatch]);

  const handleNewRow = async () => {
    const newUuid = uuidv4();

    let layer = {
      id: newUuid,
      referenceZone: "MONT A1",
      onProdDate: new Date().toISOString(),
      azimuth: 0,
      hzLength: 2500,
      minHeelOffset: 100,
      minToeOffset: 100,
      horizontalSpacingBetweenSticks: 0,
      leftHorizontalOffset: 100,
      rightHorizontalOffset: 100,
      verticalOffset: "T-0",
      includeExistingWells: false,
      layerNumber: 0
    };
    if (state.scenario.layerPlacements.length > 0) {
      const last =
        state.scenario.layerPlacements[state.scenario.layerPlacements.length - 1];
      layer = {
        ...last,
        id: newUuid
      };
    }
    try {
      await addPadLayer({ scenarioId: state.scenario?._id?.$oid, layer });
      setEditId(newUuid);
    } catch (err) {
      const axiosError = err as AxiosErrorWithData;
      const errormsg = axiosError?.response?.data?.error ?? "An error occurred";
      showToast("scenario-container", errormsg, ToastType.Error);
    }
  };

  const handleRunScenario = async () => {
    try {
      await runScenario(state.scenario?._id?.$oid);
    } catch (err) {
      showError(err);
    }
  };

  const handleExportScenario = async () => {
    setIsExporting(true);
    try {
      await exportScenarios();
      showToast(
        "scenario-container",
        "Scenarios exported successfully.",
        ToastType.Success
      );
    } catch (err) {
      showError(err);
    } finally {
      setIsExporting(false);
      setPopoverOpen(false);
    }
  };

  const showError = (err: unknown) => {
    const axiosError = err as AxiosErrorWithData;
    const msg = axiosError?.response?.data?.error ?? "An error occurred";
    showToast("scenario-container", msg, ToastType.Error);
  };

  return {
    /* state */
    showNewScenarioModal,
    setShowNewScenarioModal,
    popoverOpen,
    setPopoverOpen,
    editId,
    isLoading,
    isExporting,

    /* actions */
    handleNewRow,
    handleRunScenario,
    handleExportScenario
  };
}
